import { SeasonSelect } from "@/components/ui/season-select";
import React from "react";
import { Check, Image as ImageIcon, Plus, Search, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useToast } from "@/components/ui/use-toast";
import { fetchFamilies, fetchManufacturingProducts, fetchSeasons, formatMoney } from "@/lib/api/manufacturing";

const statusLabels = {
  active: "Activo",
  obsolete: "Obsoleto",
};

function getMainImage(product) {
  const images = Array.isArray(product.images) ? product.images : [];
  return images[product.main_image_index || 0] || images[0] || null;
}

export function SimulationProductPicker({ onAddProduct, disabledProductIds = [], productionOrderMode = false, compact = productionOrderMode }) {
  const [products, setProducts] = React.useState([]);
  const [searchTerm, setSearchTerm] = React.useState("");
  const [statusFilter, setStatusFilter] = React.useState("active");
  const [temporadaFilter, setTemporadaFilter] = React.useState("");
  const [familyFilter, setFamilyFilter] = React.useState("");
  const [seasons, setSeasons] = React.useState([]);
  const [families, setFamilies] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const { toast } = useToast();

  const [appliedFilters, setAppliedFilters] = React.useState(null);
  const query = compact ? appliedFilters : { searchTerm, status: statusFilter, temporada: temporadaFilter, codFam: familyFilter };
  const querySearch = query?.searchTerm || "";
  const queryStatus = query?.status || "active";
  const querySeason = query?.temporada || "";
  const queryFamily = query?.codFam || "";
  const hasActiveSearch = Boolean(querySearch.trim() || querySeason || queryFamily);
  const requestSequence = React.useRef(0);
  const applySearch = () => setAppliedFilters({ searchTerm, status: statusFilter, temporada: temporadaFilter, codFam: familyFilter });

  const loadProducts = React.useCallback(async () => {
    const request = ++requestSequence.current;
    if (!querySearch.trim() && !querySeason && !queryFamily) {
      setProducts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const result = await fetchManufacturingProducts({
      searchTerm: querySearch,
      status: queryStatus,
      temporada: querySeason,
      codFam: queryFamily,
      limit: 250,
    });
    if (request !== requestSequence.current) return;
    if (result.error) {
      toast({ title: "Error", description: "No se pudieron cargar los artículos", variant: "destructive" });
    }
    setProducts(result.data || []);
    setLoading(false);
  }, [querySearch, queryStatus, querySeason, queryFamily, appliedFilters, toast]);

  React.useEffect(() => {
    loadProducts();
    return () => { requestSequence.current += 1; };
  }, [loadProducts]);

  React.useEffect(() => {
    if (compact) return;
    const loadCatalogs = async () => {
      const [seasonsResult, familiesResult] = await Promise.all([fetchSeasons(), fetchFamilies()]);
      if (!seasonsResult.error) setSeasons(seasonsResult.data || []);
      if (!familiesResult.error) setFamilies(familiesResult.data || []);
    };
    loadCatalogs();
  }, [compact]);

  return (
    <div className={`${compact ? "space-y-2" : "space-y-4"} rounded-lg border bg-white p-4 shadow-sm`}>
      <div>
        <h3 className="font-semibold">{productionOrderMode ? "Añadir artículos a la orden" : "Añadir artículo a la simulación"}</h3>
        <p className="text-sm text-gray-600">Busca y selecciona artículos existentes del catálogo de fabricación.</p>
      </div>

      <div className={`grid gap-2 ${compact ? "md:grid-cols-[1fr_140px_auto]" : "md:grid-cols-[1fr_140px_180px_180px]"}`}>
        <div className="relative">
          <Input
            type="search"
            aria-label="Buscar artículos de fabricación"
            onKeyDown={(event) => { if (compact && event.key === "Enter") { event.preventDefault(); applySearch(); } }}
            placeholder="Buscar artículos de fabricación..."
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            className="pl-9"
          />
          <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
        </div>
        <select
          className="rounded-md border border-input bg-background px-3 py-2"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
        >
          <option value="active">Activos</option>
          <option value="obsolete">Obsoletos</option>
          <option value="all">Todos</option>
        </select>
        {!compact && <>
        <SeasonSelect data-season-selector="true"
          className="rounded-md border border-input bg-background px-3 py-2"
          value={temporadaFilter}
          onChange={(event) => setTemporadaFilter(event.target.value)}
        >
          <option value="">Todas las temporadas</option>
          {seasons.map((season) => (
            <option key={season.cod_tem} value={season.cod_tem.trim()}>
              {season.cod_tem.trim()} · {season.descrip}
            </option>
          ))}
        </SeasonSelect>
        <select
          className="rounded-md border border-input bg-background px-3 py-2"
          value={familyFilter}
          onChange={(event) => setFamilyFilter(event.target.value)}
        >
          <option value="">Todas las familias</option>
          {families.map((family) => (
            <option key={family.cod_fam} value={family.cod_fam.trim()}>
              {family.cod_fam.trim()} · {family.descrip}
            </option>
          ))}
        </select>
        </>}
        {compact && <Button type="button" onClick={applySearch}><Search className="mr-2 h-4 w-4" />Aplicar</Button>}
      </div>

      {!compact && (temporadaFilter || familyFilter) && (
        <div className="flex justify-end">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => {
              setTemporadaFilter("");
              setFamilyFilter("");
            }}
          >
            <X className="mr-2 h-4 w-4" />
            Limpiar filtros
          </Button>
        </div>
      )}

      <div className="grid max-h-[34rem] gap-4 overflow-y-auto pr-1 md:grid-cols-2 2xl:grid-cols-3">
        {!hasActiveSearch ? (
          <p className="text-sm text-gray-500">{compact ? "Introduce un código, descripción o filtro y pulsa Aplicar." : "Escribe un código o descripción, o selecciona una temporada o familia para buscar artículos."}</p>
        ) : loading ? (
          <p className="text-sm text-gray-500">Cargando artículos...</p>
        ) : products.length === 0 ? (
          <p className="text-sm text-gray-500">No hay artículos que coincidan con la búsqueda.</p>
        ) : (
          products.map((product) => {
            const mainImage = getMainImage(product);
            const alreadyAdded = disabledProductIds.includes(product.id);
            return (
              <div key={product.id} className="flex overflow-hidden rounded-lg border bg-white shadow-sm">
                <div className="flex h-32 w-28 flex-shrink-0 items-center justify-center bg-gray-100">
                  {mainImage ? (
                    <img src={mainImage} alt={product.description} className="h-full w-full object-cover" />
                  ) : (
                    <ImageIcon className="h-8 w-8 text-gray-400" />
                  )}
                </div>
                <div className="flex min-w-0 flex-1 flex-col justify-between gap-3 p-3">
                  <div>
                    <div className="flex items-start justify-between gap-2">
                      <h4 className="font-semibold">{product.code}</h4>
                      <span className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-700">
                        {statusLabels[product.status] || product.status}
                      </span>
                    </div>
                    <p className="line-clamp-2 text-sm text-gray-600">{product.description}</p>
                    <p className="mt-1 text-xs text-gray-500">
                      {product.season?.descrip || product.temporada || "Sin temporada"}
                      {product.family?.descrip ? ` · ${product.family.descrip}` : ""}
                    </p>
                    <p className="mt-1 text-xs font-medium text-gray-600">Tarifa 1: {formatMoney(product.precio_v)}</p>
                  </div>
                  <Button
                    type="button"
                    variant={alreadyAdded ? "outline" : "default"}
                    size="sm"
                    disabled={alreadyAdded}
                    onClick={() => {
                      onAddProduct?.(product);
                      setSearchTerm("");
                    }}
                  >
                    {alreadyAdded ? <Check className="mr-2 h-4 w-4" /> : <Plus className="mr-2 h-4 w-4" />}
                    {alreadyAdded ? "Añadido" : "Añadir"}
                  </Button>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}
